import React from 'react';
import { MessageCircle, Video, FileText, StickyNote, Heart, Award, Users, Handshake, Share2, Key, Zap, Blocks } from 'lucide-react';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white overflow-hidden pt-20 pb-12">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-20 left-10 w-32 md:w-64 h-32 md:h-64 bg-emerald-100 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 md:w-96 h-48 md:h-96 bg-blue-100 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-purple-50 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 text-center">
        {/* P2P Characters with Animated Connection - Responsive for all screens */}
        <div className="mb-8 md:mb-12 relative h-[160px] sm:h-[180px] md:h-[200px] lg:h-[240px]">
          {/* Left Character - Node 1 */}
          <div className="absolute left-2 sm:left-6 md:left-10 lg:left-20 top-1/2 -translate-y-1/2">
            <div className="relative">
              {/* Pulsing rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-blue-400 md:border-2 animate-pulse-ring-1"></div>
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-blue-300 md:border-2 animate-pulse-ring-2"></div>
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-blue-200 md:border-2 animate-pulse-ring-3"></div>
              </div>
              
              {/* Orbiting dots */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-16 sm:w-24 md:w-32 lg:w-36 h-16 sm:h-24 md:h-32 lg:h-36 animate-spin-slow">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-blue-500 rounded-full shadow-lg"></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-emerald-500 rounded-full shadow-lg"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-purple-500 rounded-full shadow-lg"></div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-pink-500 rounded-full shadow-lg"></div>
                </div>
              </div>
              
              {/* Character */}
              <img 
                src="https://customer-assets.emergentagent.com/job_collab-hub-62/artifacts/bgncf8h8_Group%20%281%29.png" 
                alt="Node 1" 
                className="relative z-10 w-16 sm:w-24 md:w-32 lg:w-36 h-16 sm:h-24 md:h-32 lg:h-36 object-contain drop-shadow-2xl hover:scale-110 transition-transform duration-300"
              />
            </div>
          </div>

          {/* Right Character - Node 2 */}
          <div className="absolute right-2 sm:right-6 md:right-10 lg:right-20 top-1/2 -translate-y-1/2">
            <div className="relative">
              {/* Pulsing rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-emerald-400 md:border-2 animate-pulse-ring-1"></div>
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-emerald-300 md:border-2 animate-pulse-ring-2"></div>
                <div className="absolute w-20 sm:w-28 md:w-40 lg:w-44 h-20 sm:h-28 md:h-40 lg:h-44 rounded-full border border-emerald-200 md:border-2 animate-pulse-ring-3"></div>
              </div>
              
              {/* Orbiting dots */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-16 sm:w-24 md:w-32 lg:w-36 h-16 sm:h-24 md:h-32 lg:h-36 animate-spin-slow-reverse">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-emerald-500 rounded-full shadow-lg"></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-orange-500 rounded-full shadow-lg"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-amber-500 rounded-full shadow-lg"></div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-blue-500 rounded-full shadow-lg"></div>
                </div>
              </div>
              
              {/* Character */}
              <img 
                src="https://customer-assets.emergentagent.com/job_collab-hub-62/artifacts/cwiqjq3y_Group%2038.png" 
                alt="Node 2" 
                className="relative z-10 w-16 sm:w-24 md:w-32 lg:w-36 h-16 sm:h-24 md:h-32 lg:h-36 object-contain drop-shadow-2xl hover:scale-110 transition-transform duration-300"
              />
            </div>
          </div>

          {/* Animated Icons Flowing Between Characters */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
            {/* Chat Icon */}
            <div className="absolute animate-flow-icon-1" style={{ left: '18%', top: '25%' }}>
              <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-500 drop-shadow-lg" />
            </div>

            {/* Video Icon */}
            <div className="absolute animate-flow-icon-2" style={{ left: '18%', top: '45%' }}>
              <Video className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-purple-500 drop-shadow-lg" />
            </div>

            {/* File Icon */}
            <div className="absolute animate-flow-icon-3" style={{ left: '18%', top: '65%' }}>
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-emerald-500 drop-shadow-lg" />
            </div>

            {/* Docs/Notes Icon */}
            <div className="absolute animate-flow-icon-reverse-1" style={{ right: '18%', top: '30%' }}>
              <StickyNote className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-500 drop-shadow-lg" />
            </div>

            {/* Engagement Icon */}
            <div className="absolute animate-flow-icon-reverse-2" style={{ right: '18%', top: '50%' }}>
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-pink-500 drop-shadow-lg" />
            </div>

            {/* Reward Icon */}
            <div className="absolute animate-flow-icon-reverse-3" style={{ right: '18%', top: '70%' }}>
              <Award className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-orange-500 drop-shadow-lg" />
            </div>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight leading-tight px-2">
          <span className="block md:inline">Your complete toolkit for</span>{' '}
          <span className="relative inline-block mt-2 md:mt-0">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">private digital spaces</span>
            <span className="absolute -bottom-1 md:-bottom-2 left-0 right-0 h-0.5 md:h-1 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 rounded-full"></span>
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-8 md:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
          Empowering everyone with private workspaces and InteractionFi.
        </p>

        {/* Professional View Boxes - Grid on Mobile, Flex on Desktop */}
        <div className="grid grid-cols-3 md:flex md:flex-wrap items-center justify-center gap-2 md:gap-3 max-w-5xl mx-auto px-2 md:px-4">
          {/* Connect */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-1">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Connect</span>
            </div>
          </div>

          {/* Collaborate */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-2">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Handshake className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Collaborate</span>
            </div>
          </div>

          {/* Share */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-3">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Share2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Share</span>
            </div>
          </div>

          {/* Own */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-4">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Key className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Own</span>
            </div>
          </div>

          {/* Engage */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-5">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Engage</span>
            </div>
          </div>

          {/* Build */}
          <div className="bg-white border border-gray-200 rounded-lg px-3 md:px-5 py-2 md:py-3 shadow-sm transition-all duration-500 animate-fade-in-up box-highlight-6">
            <div className="flex items-center justify-center md:justify-start gap-1.5 md:gap-2">
              <Blocks className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-700" strokeWidth={2} />
              <span className="text-xs md:text-sm font-medium text-gray-700">Build</span>
            </div>
          </div>
        </div>
      </div>

      {/* Smooth transition curve to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24 bg-gradient-to-b from-transparent to-white"></div>
    </section>
  );
};

export default HeroSection;
